import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { authService, TelegramSessionExpiredError } from "../../src/entities/user";
import { useTranslation } from "../../src/shared/config/i18n";
import { isHttpError } from "../../src/shared/api/http";
import { TelegramWebAppUtils } from "../../src/shared/api/telegram";
import { isDevMode } from "../../src/shared/config/dev-mode";
import { decodeJwt, isAccessTokenExpired } from "../../src/shared/utils/jwt";
import { listWallets } from "../../src/shared/api/wallet";
import { listNfts } from "../../src/shared/api/nft";
import { walletsKeys } from '../../src/features/wallets';
import { nftsKeys } from '../../src/features/nfts';
import { mapWalletResponseToEntity } from "../../src/entities/wallet";
import { mapNftResponseToEntity } from "../../src/entities/nft";
import { logger } from "../../src/shared/utils/logger";
import { dismissStartupLoader } from "../../src/shared/utils/startup-loader";
import type { TokenPairDto } from "../../src/shared/contracts/model/tokenPairDto";

type AuthStatus = "loading" | "authenticated" | "error";

interface AuthLayoutProps {
  children: React.ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => authService.subscribeSessionError(error => {
    if (!error) return;
    // Stop authenticated polling when the original Telegram launch can no
    // longer restore the session; reuse the existing authentication error UI.
    setErrorMessage(t('auth.telegramSessionExpired'));
    setStatus('error');
  }), [t]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let cancelled = false;

    const authenticate = async () => {
      try {
        // In dev mode, skip Telegram checks
        if (isDevMode()) {
          logger.log("[AuthLayout] Dev mode: skipping Telegram authentication");

          await authService.loadTokens();
          let currentTokens = authService.getTokens();

          // If no tokens exist, create mock tokens
          if (!currentTokens) {
            // Use mock initData for dev mode
            const mockInitData = "user=%7B%22id%22%3A123456789%2C%22first_name%22%3A%22Dev%22%2C%22last_name%22%3A%22User%22%2C%22username%22%3A%22dev_user%22%7D&auth_date=" + Math.floor(Date.now() / 1000) + "&hash=mock_hash";
            currentTokens = await authService.signInWithInitData(mockInitData);
          } else if (isAccessTokenExpired(currentTokens.accessToken)) {
            try {
              const { tokens } = await authService.refreshTokens(
                currentTokens.refreshToken,
              );
              currentTokens = tokens;
            } catch (refreshError) {
              logger.warn(
                "[AuthLayout] Refresh failed, creating new mock tokens",
                refreshError,
              );
              const mockInitData = "user=%7B%22id%22%3A123456789%2C%22first_name%22%3A%22Dev%22%2C%22last_name%22%3A%22User%22%2C%22username%22%3A%22dev_user%22%7D&auth_date=" + Math.floor(Date.now() / 1000) + "&hash=mock_hash";
              currentTokens = await authService.signInWithInitData(mockInitData);
            }
          }

          if (cancelled) return;
          setStatus("authenticated");
          return;
        }

        await authService.ensureTelegramReady();

        const initData = TelegramWebAppUtils.getInitData();
        if (!initData) {
          throw new Error(
            "Telegram initData is missing. Open the app inside Telegram Mini Apps.",
          );
        }

        await authService.loadTokens();
        let currentTokens = authService.getTokens();

        const expectedTelegramId = TelegramWebAppUtils.getInitDataUnsafe()?.user?.id;

        const checkTokensMatchInitData = (tokens: TokenPairDto | null): boolean => {
          if (!tokens || !expectedTelegramId) return true; // If no tokens or no expected ID, consider it OK
          try {
            const payload = decodeJwt(tokens.accessToken);
            const tokenTelegramId = payload?.telegramId ?? payload?.telegram_id;
            if (tokenTelegramId && Number(tokenTelegramId) !== Number(expectedTelegramId)) {
              logger.warn(
                `[AuthLayout] Telegram user changed: token has ${tokenTelegramId}, expected ${expectedTelegramId}. Clearing tokens.`,
              );
              return false;
            }
            return true;
          } catch (error) {
            logger.warn("[AuthLayout] Failed to decode token, clearing tokens", error);
            return false;
          }
        };

        // Always check if tokens match current Telegram user
        if (currentTokens && !checkTokensMatchInitData(currentTokens)) {
          // Telegram user changed, clear old tokens
          logger.log("[AuthLayout] Telegram user changed, clearing all tokens");
          await authService.clearTokens();
          
          // Also clear telecom tokens as they are user-specific
          try {
            const { telecomTokenService } = await import('../../src/entities/user');
            await telecomTokenService.clearToken();
            
            const { telecomTokenByPhoneService } = await import('../../src/entities/user/telecom-token-by-phone-service');
            telecomTokenByPhoneService.clearAllTokens();
            
            logger.log("[AuthLayout] Cleared telecom tokens");
          } catch (error) {
            logger.warn("[AuthLayout] Failed to clear telecom tokens", error);
          }
          
          currentTokens = null;
        }

        if (!currentTokens) {
          // No tokens or tokens cleared due to user change
          currentTokens = await authService.signInWithInitData(initData);
        } else if (isAccessTokenExpired(currentTokens.accessToken)) {
          try {
            const { tokens } = await authService.refreshTokens(
              currentTokens.refreshToken,
            );
            // After refresh, check again if tokens match current user
            if (!checkTokensMatchInitData(tokens)) {
              logger.warn(
                "[AuthLayout] Refreshed tokens don't match current Telegram user, re-authenticating",
              );
              await authService.clearTokens();
              
              // Also clear telecom tokens
              try {
                const { telecomTokenService } = await import('../../src/entities/user');
                await telecomTokenService.clearToken();
                
                const { telecomTokenByPhoneService } = await import('../../src/entities/user/telecom-token-by-phone-service');
                telecomTokenByPhoneService.clearAllTokens();
              } catch (error) {
                logger.warn("[AuthLayout] Failed to clear telecom tokens", error);
              }
              
              currentTokens = await authService.signInWithInitData(initData);
            } else {
              currentTokens = tokens;
            }
          } catch (refreshError) {
            if (!isHttpError(refreshError) || refreshError.status !== 401) throw refreshError;
            logger.warn(
              "[AuthLayout] Refresh failed, falling back to re-auth",
              refreshError,
            );
            await authService.clearTokens();
            currentTokens = await authService.signInWithInitData(initData);
          }
        }

        if (cancelled) return;
        setStatus("authenticated");
      } catch (error) {
        if (cancelled) return;
        logger.error("[AuthLayout] authentication failed", error);
        if (isHttpError(error) && error.status === 401) {
          await authService.clearTokens();
          // Only a rejected credential warrants clearing related sessions.
          try {
            const { telecomTokenService } = await import('../../src/entities/user');
            await telecomTokenService.clearToken();
            const { telecomTokenByPhoneService } = await import('../../src/entities/user/telecom-token-by-phone-service');
            telecomTokenByPhoneService.clearAllTokens();
          } catch (clearError) {
            logger.warn("[AuthLayout] Failed to clear telecom tokens on auth error", clearError);
          }
        }
        
        if (cancelled) return;
        setErrorMessage(
          error instanceof TelegramSessionExpiredError
            ? t('auth.telegramSessionExpired')
            : error instanceof Error ? error.message : t('auth.failed'),
        );
        setStatus("error");
      }
    };

    authenticate();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (status !== "loading") {
      dismissStartupLoader();
    }
  }, [status]);

  useEffect(() => {
    if (status !== "authenticated") return;

    const prefetchData = async () => {
      const STALE_TIME = 5 * 60 * 1000;

      const scheduleIdleCallback = (callback: () => void) => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(callback, { timeout: 2000 });
        } else {
          setTimeout(callback, 1);
        }
      };

      scheduleIdleCallback(async () => {
        await Promise.all([
          queryClient.prefetchQuery({
            queryKey: walletsKeys.all,
            queryFn: async () => {
              const response = await listWallets();
              return response.map(mapWalletResponseToEntity);
            },
            staleTime: STALE_TIME,
          }),

          queryClient.prefetchInfiniteQuery({
            queryKey: nftsKeys.list(),
            queryFn: async () => {
              const response = await listNfts({ limit: 20 });
              return {
                items: response.items.map(mapNftResponseToEntity),
                nextCursor: response.nextCursor,
              };
            },
            initialPageParam: undefined,
            getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
            pages: 1,
            staleTime: STALE_TIME,
          }),
        ]);

        scheduleIdleCallback(async () => {
          await Promise.all([
            (async () => {
              const { getMarketNfts } = await import('../../src/shared/api/market');
              const { marketKeys } = await import('../../src/features/market/api/market.service');
              return queryClient.prefetchQuery({
                queryKey: marketKeys.nfts(),
                queryFn: () => getMarketNfts(),
                staleTime: STALE_TIME,
              });
            })(),

          ]);
        });
      });
    };

    prefetchData();
  }, [status, queryClient]);

  if (status === "loading") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
        <span className="text-lg font-medium">
          {isDevMode() && "Initializing dev mode..."}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {isDevMode() && "Using mock authentication for development."}
        </span>
        {isDevMode() && (
          <div className="mt-4 rounded-lg bg-yellow-100 p-3 text-xs text-yellow-800">
            <strong>Dev Mode Active</strong>
            <br />
            Running without Telegram WebApp
          </div>
        )}
      </main>
    );
  }

  if (status === "error") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
        <span className="text-lg font-medium text-red-500">{t('auth.errorTitle')}</span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {errorMessage ?? t('auth.failed')}
        </span>
        <button type="button" onClick={() => window.location.reload()}>
          {t('common.retry')}
        </button>
      </main>
    );
  }

  return <>{children}</>;
}
